import React, { useState, useEffect, useContext } from 'react';
import { observer } from 'mobx-react-lite';
import { withRouter } from 'react-router-dom';

import clsx from 'clsx';
import LoaderBounce from 'components/loaders/LoaderBounce';
import { statsCtx } from 'pages/assessment/statsStore';
import { checkHasUserPermission } from 'helper/commonFunctions';
import HireBanner from 'components/candidate_cards/CandidateCardWrapper/HireBanner';
import CandidatePhotoInfo from './CandidatePhotoInfo';
import CandidateStatusInfo from './CandidateStatusInfo';
import CommentsInfo from './CommentsInfo';
import ReportCardHeader from './ReportCardHeader';
import TagsInfo from './TagsInfo';
import CardScoreBlock from './CardScoreBlock';
import CardPerformanceBlock from './CardPerformanceBlock';
import GradeBlock from './GradeBlock';
import FraudDetectedBanner from './WarningBanner/FraudDetectedBanner';
import CheatDetectedBanner from './WarningBanner/CheatDetectedBanner';
import ManualGradingRequiredBanner from './WarningBanner/ManualGradingRequiredBanner';
import NotCompletedBlock from './NotCompletedBlock';
import SatisfactionBlock from './SatisfactionBlock';
import ProctoringImages from './ProctoringImages';
import ExtendRequestedBlock from './ExtendRequestedBlock';
import StatusHistory from './StatusHistory';
import CompletionsHistory from './CompletionsHistory';
import useStyles from './styles';
import { getCandidateProfile } from '../../../requests/APIrequests';
import SummaryBlock from './SummaryBlock';


const CandidateCardWrapper = observer((props) => {
    const {
        className = '', getUserInfo, company, isLoading,
        user, setUser, handleCloseCard, context = 'report card',
        opportunitySlug, audition, audition: { uuid, name }, isLoadingAudition,
        getTags, companyTags, setTags, isEmployerSortEnabled,
        loadCandidatePosts, propPublic, authorizeGuest, candidatesProp, gradeableCard,
        addCandidateComment, loadGradeQuestion, gradeFunction, isIntegration,
        getAuditionQuestionResults, ReportHeaderComp, hideCandidateDetails,
        onHireChanges, loadFullStats, roleType,
        getAuditionScreeningQuestionResults, onDeleteCallback, userAudition, refreshOpportunities, isRestoreLoading
    } = props;
    const [showHireBanner, setShowHireBanner] = useState(false);
    const [profile, setProfile] = useState({});
    const { increaseShortlistCandidatesCount } = useContext(statsCtx);

    const classes = useStyles();

    useEffect(() => {
        setShowHireBanner(false);
    }, [user && user.id]);


    const dataForEvents = {
        candidateId: user ? user.candidateUuid : null,
        ttId: uuid,
        ttName: name,
        context
    };

    const updateUserFields = (updatedFields) => {
        setUser({ ...user, ...updatedFields });
    };

    const getUserProfile = (userUuid, callback = () => { }) => {
        getCandidateProfile(userUuid, Boolean(propPublic))
            .then((response) => {
                const { success, data } = response;
                if (success) {
                    setProfile(data);
                }
            })
            .finally(callback);
    };

    const handleShowBanner = () => {
        setShowHireBanner(!showHireBanner);
    };

    if (isLoading) {
        return (
            <div className="u-txt--center u-pdn--tx10 u-pdn--bx10">
                <LoaderBounce />
            </div>
        );
    }

    if (!user) return null;
    const isIntegrationUser = roleType === 'integration';
    const { permissions, active } = audition;
    const { manualGradeMissing } = user;

    const edit = checkHasUserPermission(company, permissions, 'edit');

    return (
        <div className={clsx(classes.wrapper, className)}>
            <ReportCardHeader
                audition={audition}
                opportunitySlug={opportunitySlug}
                candidate={user}
                propPublic={propPublic}
                handleCloseCard={handleCloseCard}
                ReportHeaderComp={ReportHeaderComp}
            />
            <div className={classes.userInfo}>
                <div className="userInfo">
                    <CandidatePhotoInfo
                        {...{
                            user,
                            context,
                            setUser,
                            audition,
                            company,
                            propPublic,
                            handleCloseCard,
                            getUserInfo,
                            opportunitySlug,
                            showHireBanner,
                            setShowHireBanner,
                            hideCandidateDetails,
                            getUserProfile,
                            increaseShortlistCandidatesCount,
                            isIntegration,
                            onHireChanges,
                            loadFullStats,
                            onDeleteCallback,
                            roleType,
                            refreshOpportunities
                        }}
                    />
                </div>
                <div className="statusInfo">
                    <CandidateStatusInfo
                        dataForEvents={dataForEvents}
                        user={user}
                        company={company}
                        permissions={permissions}
                        updateUserFields={updateUserFields}
                        showHireBanner={showHireBanner}
                        setShowHireBanner={setShowHireBanner}
                        hideCandidateDetails={hideCandidateDetails}
                        profile={profile}
                        getUserProfile={getUserProfile}
                        isIntegration={isIntegration}
                        propPublic={propPublic}
                        audition={audition}
                    />
                </div>
                <div className="performanceInfo">
                    <CardPerformanceBlock
                        candidate={user}
                        gradeableCard={gradeableCard}
                        isEmployerSortEnabled={isEmployerSortEnabled}
                    />
                </div>
            </div>
            <div className={classes.banners}>
                {
                    (!propPublic && !isLoadingAudition && active && !isIntegrationUser) && (
                        <ExtendRequestedBlock
                            candidate={user}
                            audition={audition}
                            getUserInfo={getUserInfo}
                            disabled={!edit}
                        />
                    )
                }
                {
                    !propPublic && (
                        <>
                            <FraudDetectedBanner
                                candidate={user}
                                audition={audition}
                                getUserInfo={getUserInfo}
                            />
                            <CheatDetectedBanner
                                candidate={user}
                                company={company}
                                audition={audition}
                                getUserInfo={getUserInfo}
                            />
                        </>
                    )
                }
                {
                    (manualGradeMissing && user.completed) && (
                        <ManualGradingRequiredBanner />
                    )
                }
                <HireBanner
                    user={user}
                    audition={audition}
                    showBanner={showHireBanner && !isIntegration}
                    handleShow={handleShowBanner}
                    updateUserFields={updateUserFields}
                />
            </div>
            {
                user.candidateAiSummary && !user.archived && user.score && (
                    <SummaryBlock user={user} />
                )
            }
            {
                user.completed ? (
                    <>
                        {
                            !(gradeableCard && !user.score) && !manualGradeMissing && (
                                <CardScoreBlock
                                    propPublic={propPublic}
                                    candidate={user}
                                />
                            )
                        }
                    </>
                ) : <>{ (!isLoadingAudition && !isRestoreLoading) ? <NotCompletedBlock audition={audition} /> : null }</>
            }
            <div className="u-mrg--tx5">
                {
                    (user.completed || user.screeningQuestions) && (
                        <GradeBlock
                            audition={audition}
                            candidate={user}
                            propPublic={propPublic}
                            isIntegration={isIntegration}
                            gradeFunction={gradeFunction}
                            updateUserAuditionAndBox={getUserInfo}
                            loadGradeQuestion={loadGradeQuestion}
                            gradeableCard={gradeableCard}
                            authorizeGuest={authorizeGuest}
                            getAuditionResults={getAuditionQuestionResults}
                            getAuditionScreeningResults={getAuditionScreeningQuestionResults}
                            userAudition={userAudition}
                            hideCandidateDetails={hideCandidateDetails}
                        />
                    )
                }
            </div>
            <TagsInfo
                candidate={user}
                audition={audition}
                getTags={getTags}
                setTags={setTags}
                context={context}
                companyTags={companyTags}
                propPublic={propPublic}
                isIntegration={isIntegration}
            />
            {
                !hideCandidateDetails && user.proctoring && <ProctoringImages userAuditionId={user.userAuditionId} propPublic={propPublic} />
            }
            {
                !candidatesProp && (gradeableCard || !propPublic) && (
                    <CommentsInfo
                        user={user}
                        audition={audition}
                        opportunitySlug={opportunitySlug}
                        loadCandidatePosts={loadCandidatePosts}
                        propPublic={propPublic}
                        authorizeGuest={authorizeGuest}
                        addCandidateComment={addCandidateComment}
                    />
                )
            }
            {
                user.completed && (
                    <SatisfactionBlock
                        audition={audition}
                        candidate={user}
                        propPublic={propPublic}
                    />
                )
            }
            {
                !propPublic && !isIntegration && Boolean(user.completionsHistory?.length) && (
                    <div className="u-mrg--tx4">
                        <CompletionsHistory
                            completionsHistory={user.completionsHistory}
                            fullName={user.fullName}
                            commonProps={props}
                        />
                    </div>
                )
            }
            {
                user.statusHistory && (
                    <div className="u-mrg--tx4">
                        <StatusHistory statusHistory={user.statusHistory} />
                    </div>
                )
            }
        </div>
    );
});

export default withRouter(CandidateCardWrapper);
